import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';

interface PlanLimits {
  projects: number;
  teamMembers: number;
  storageGB: number;
}

interface PlanUsage {
  projects: number;
  teamMembers: number;
  storageGB: number;
}

const PLAN_LIMITS: Record<string, PlanLimits> = {
  starter: {
    projects: 1,
    teamMembers: 1,
    storageGB: 1,
  },
  pro: {
    projects: 999, // unlimited
    teamMembers: 3,
    storageGB: 20,
  },
  enterprise: {
    projects: 999, // unlimited
    teamMembers: 999, // unlimited
    storageGB: 100,
  },
  // Legacy plans (keep for existing subscribers)
  essencial: {
    projects: 5,
    teamMembers: 1,
    storageGB: 5,
  },
  profissional: {
    projects: 15,
    teamMembers: 3,
    storageGB: 20,
  },
};

export const usePlanLimits = () => {
  const { user } = useAuth();
  const [plan, setPlan] = useState<string>('pro');
  const [limits, setLimits] = useState<PlanLimits>(PLAN_LIMITS.pro);
  const [usage, setUsage] = useState<PlanUsage>({
    projects: 0,
    teamMembers: 1,
    storageGB: 0,
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadPlanInfo = async () => {
      try {
        // Check if user is admin
        const { data: hasAdminRole } = await supabase.rpc('has_role', { 
          _user_id: user.id, 
          _role: 'admin' 
        });

        if (hasAdminRole) {
          setIsAdmin(true);
          setLoading(false);
          return;
        }

        // Get user's subscription plan
        const { data: subData } = await supabase
          .from('subscribers')
          .select('plan, subscribed, trial_ends_at')
          .eq('user_id', user.id)
          .maybeSingle();

        // Usuários em trial recebem funcionalidades do plano Essencial
        const now = new Date();
        const trialActive = subData?.trial_ends_at ? new Date(subData.trial_ends_at) >= now : false;
        const userPlan = (trialActive && !subData?.subscribed) ? 'essencial' : (subData?.plan || 'essencial');
        
        setPlan(userPlan);
        setLimits(PLAN_LIMITS[userPlan] || PLAN_LIMITS.essencial);

        // Get current usage
        const { count: projectCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        setUsage(prev => ({
          ...prev,
          projects: projectCount || 0,
        }));

      } catch (error) {
        console.error('Error loading plan info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPlanInfo();
  }, [user]);

  const checkLimit = async (limitType: keyof PlanLimits): Promise<boolean> => {
    if (isAdmin) return true;

    const currentUsage = usage[limitType];
    const limit = limits[limitType];

    if (currentUsage >= limit) {
      const messages = {
        projects: `Você atingiu o limite de ${limit} projetos do plano ${plan === 'essencial' ? 'Essencial' : 'Profissional'}. Faça upgrade para criar mais projetos!`,
        teamMembers: `Você atingiu o limite de ${limit} membro(s) de equipe do plano ${plan === 'essencial' ? 'Essencial' : 'Profissional'}. Faça upgrade para adicionar mais membros!`,
        storageGB: `Você atingiu o limite de ${limit}GB de armazenamento do plano ${plan === 'essencial' ? 'Essencial' : 'Profissional'}. Faça upgrade para mais espaço!`,
      };

      toast.error(messages[limitType], {
        description: plan === 'essencial' ? 'Upgrade para o Plano Profissional' : 'Entre em contato para planos empresariais',
        action: plan === 'essencial' ? {
          label: 'Fazer Upgrade',
          onClick: () => window.location.href = '/assinatura',
        } : undefined,
      });

      return false;
    }

    return true;
  };

  const getUsagePercentage = (limitType: keyof PlanLimits): number => {
    if (isAdmin) return 0;
    return (usage[limitType] / limits[limitType]) * 100;
  };

  const refreshUsage = async () => {
    if (!user || isAdmin) return;

    try {
      const { count: projectCount } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setUsage(prev => ({
        ...prev,
        projects: projectCount || 0,
      }));
    } catch (error) {
      console.error('Error refreshing usage:', error);
    }
  };

  return {
    plan,
    limits,
    usage,
    isAdmin,
    loading,
    checkLimit,
    getUsagePercentage,
    refreshUsage,
  };
};
